import React, { useRef } from 'react'
import { connect } from 'react-redux'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import { Row, Col, FormGroup, Label, Input, Button, FormFeedback, Spinner, Alert } from 'reactstrap'
import { useParams } from 'react-router-dom'
import { Formik, Field } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import HeaderRow from '../../Shared/LayoutComponents/HeaderRow'
import { handleSaveCompanySettings } from '../../actions/companyActions'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import SelectField from './SelectField'
import Checkbox from './Checkbox'
import useFetchingSettings from './useFetchingSettings'

const registerSchema = Yup.object().shape({
  applicationId: Yup.string(),
  applicationSecret: Yup.string(),
  directoryId: Yup.string(),
  syncAgreement: Yup.bool(),
})

let StyledLabel = styled(Label)`
  font-weight: 500;
`

export const Settings = (props) => {
  let params = useParams()
  let isAdmin = props.isAdmin;

  useFetchingSettings(params.companyId, isAdmin);

  const formRef = useRef();

  let domainExpireyThreshold = new Date();
  domainExpireyThreshold = domainExpireyThreshold.setDate(domainExpireyThreshold.getDate() + 21);

  if (props.loading) {
    return <LoadingSpinner />
  } else {
    return (
      <PageContainer>
        <HeaderRow>
          <Col sm={12} md={9}>
            <PageHeading small>Settings</PageHeading>
          </Col>
        </HeaderRow>
        <Row>
          <Col sm={0} md={1}></Col>
          <Col sm={12} md={9}>
            <Formik
              initialValues={props.selectedCompanySettings}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                try {
                  await props.handleSaveCompanySettings(values.id, values, isAdmin)
                  setSubmitting(false)
                } catch (e) {
                  setSubmitting(false)
                  setStatus({ msg: e })
                }
              }}
              validationSchema={registerSchema}
              innerRef={formRef}
            >
              {(props) => {
                const {
                  values,
                  touched,
                  errors,
                  isSubmitting,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  setFieldTouched,
                  setValues,
                  status,
                } = props
                return (
                  <form onSubmit={handleSubmit}>
                    {isAdmin && (
                      <>
                        <FormGroup row>
                          <StyledLabel sm={3} for="applicationId">
                            Application (client) ID:
                          </StyledLabel>
                          <Col sm={9}>
                            <Input
                              autoComplete="off"
                              invalid={errors.applicationId && touched.applicationId ? true : false}
                              type="text"
                              value={values ? values.applicationId : ''}
                              name="applicationId"
                              id="applicationId"
                              placeholder="Enter Application ID"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <FormFeedback>{errors.applicationID}</FormFeedback>
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <StyledLabel sm={3} for="applicationSecret">
                            Application Secret:
                          </StyledLabel>
                          <Col sm={9}>
                            <Input
                              autoComplete="off"
                              invalid={errors.applicationSecret && touched.applicationSecret ? true : false}
                              value={values ? values.applicationSecret : ''}
                              type="password"
                              name="applicationSecret"
                              id="applicationSecret"
                              placeholder="Enter Application Secret"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <FormFeedback>{errors.applicationSecret}</FormFeedback>
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <StyledLabel sm={3} for="directoryId">
                            Directory (tenant) ID:
                          </StyledLabel>
                          <Col sm={9}>
                            <Input
                              autoComplete="off"
                              invalid={errors.directoryId && touched.directoryId ? true : false}
                              type="text"
                              value={values ? values.directoryId : ''}
                              name="directoryId"
                              id="directoryId"
                              placeholder="Enter Directory ID"
                              onChange={(name, value) => {
                                var newValues = { ... values };
                                newValues.domains = values.domains.map(d => {
                                  let newDomain = { ...d };
                                  newDomain.isExcluded = false;
                                  return newDomain;
                                });
                                newValues.directoryIdHasChanged = true;
                                setValues(newValues);
                                handleChange(name, value);
                              }}
                              onBlur={handleBlur}
                            />
                            <FormFeedback>{errors.directoryId}</FormFeedback>
                          </Col>
                        </FormGroup>
                      </>
                    )}
                    
                    {(!(values?.domains?.every(d => d.isExcluded == false || d.exclusionExpires === null | d.exclusionExpires === 0 || d.exclusionExpires > domainExpireyThreshold) ?? true)) && (
                      <Col sm={12}>
                        <Alert color="danger" >
                          <h4 className="alert-heading">Some Domains Exclusions are Due to Expire</h4>
                          <p>Please confirm that the exclusion for the below domains should remain in place by pressing "Confirm".<br/><b>Press save to apply changes</b></p>
                          <ul>
                            {values.domains
                              .filter(d => d.isExcluded && d.exclusionExpires !== null && d.exclusionExpires !== 0 && d.exclusionExpires <= domainExpireyThreshold)
                              .map(d => <li>{d.fqdn} ({new Date(d.exclusionExpires).toLocaleDateString()}) <Button onClick={(a, b) => {
                                var newValues = { ... values };
                                newValues.domains = values.domains.map(dd => {
                                  if(dd.fqdn != d.fqdn)
                                    return dd;
                                  
                                  let newDomain = { ...dd };
                                  newDomain.exclusionExpires = 0;
                                  return newDomain;
                                });
                                setValues(newValues);
                                formRef.current.handleSubmit();
                              }}>Confirm</Button></li>)}
                          </ul>
                        </Alert>
                      </Col>
                    )}
                    <FormGroup row>
                      <Col sm={12}>
                        <SelectField
                          id="excludedDomains"
                          name="excludedDomains"
                          label="Excluded Domains"
                          placeholder="Select Domains"
                          options={values && values.domains ? values.domains.map(d => {
                            return { value: d.fqdn, label: decorateDomainLabel(d) }
                          }) : null }
                          value={values && values.domains ? values.domains.filter(d => d.isExcluded).map(d => { return { value: d.fqdn, label: decorateDomainLabel(d) }}) : null}
                          isMulti={true}
                          onChange={(name, excludedDomains) => {
                            var newValues = { ... values };
                            newValues.domains = values.domains.map(d => {
                              let newDomain = { ...d };
                              newDomain.isExcluded = excludedDomains ? excludedDomains.some(ed => ed.value == d.fqdn) : false;
                              return newDomain;
                            });
                            setValues(newValues);
                          }}
                          onBlur={setFieldTouched}
                          touched={touched.selectedExcludedAdditions}
                          isDisabled={values?.directoryIdHasChanged ?? false}
                          error={errors.selectedExcludedAdditions}
                          isClearable={false}
                          backspaceRemovesValue={true}
                         />
                      </Col>
                    </FormGroup>
                    {isAdmin && (
                      <>
                        <FormGroup row>
                          <Col sm={12}>
                            <SelectField
                              id="selectedExcludedAdditions"
                              name="selectedExcludedAdditions"
                              label="Excluded Additions"
                              placeholder="Select Additions"
                              options={values ? values.excludedAdditions : null}
                              value={values ? values.selectedExcludedAdditions : null}
                              isMulti={true}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              touched={touched.selectedExcludedAdditions}
                              error={errors.selectedExcludedAdditions}
                              isClearable={true}
                              backspaceRemovesValue={true}
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Col sm={12}>
                            <SelectField
                              id="selectedAgreement"
                              name="selectedAgreement"
                              label="Synced Agreement"
                              placeholder="Select Agreement"
                              options={values ? values.agreements : null}
                              value={values ? values.selectedAgreement : null}
                              isMulti={false}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              touched={touched.agreements}
                              error={errors.agreements}
                              isClearable={true}
                              backspaceRemovesValue={true}
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Col sm={12}>
                            <Field
                              name="syncAgreement"
                              type="checkbox"
                              fieldName="Sync to Agreement"
                              checked={values ? values.syncAgreement : false}
                              component={Checkbox}
                            />
                          </Col>
                        </FormGroup>
                      </>
                    )}
                    <Row>{status && status.msg && <div>{status.msg}</div>}</Row>
                    <Button className="float-right" type="submit" disabled={isSubmitting} color="success">
                      {isSubmitting ? (
                        <span>
                          <Spinner style={{ height: '1.15rem', width: '1.15rem' }} color="light" /> Saving
                        </span>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </form>
                )
              }}
            </Formik>
          </Col>
          <Col sm={0} md={1}></Col>
        </Row>
      </PageContainer>
    )
  }
}

const mapStateToProps = (state) => ({
  loading: state.company.loadingSettings,
  selectedCompany: state.company.selectedCompany,
  selectedCompanySettings: state.company.selectedCompanySettings,
})

const mapDispatchToProps = {
  handleSaveCompanySettings,
}

function decorateDomainLabel(domain)
{
  let extras = [];
  if(domain.isDefault)
    extras.push("Default");
  if(domain.isInitial)
    extras.push("Initial");
  if(!domain.isVerified)
    extras.push("Unverified");

  if(extras.length){
    return `${domain.fqdn} (${extras.join(', ')})`;
  }

  return domain.fqdn;
}

export default connect(mapStateToProps, mapDispatchToProps)(Settings)
